import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, onSuccess, mode: initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    postcode: '',
    city: '',
    accountType: 'client',
    agreeToTerms: false,
    agreeToPrivacy: false,
    emailUpdates: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signIn, signUp } = useAuth();

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (mode === 'register') {
      if (!formData?.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData?.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData?.postcode?.trim()) {
        newErrors.postcode = 'Postcode is required';
      }
      if (!formData?.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms and conditions';
      }
      if (!formData?.agreeToPrivacy) {
        newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      if (mode === 'login') {
        const { data, error } = await signIn(formData?.email, formData?.password);

        if (error) {
          setErrors({ submit: error });
        } else if (data?.user) {
          onSuccess();
          onClose();
        }
      } else {
        // Registration
        const registrationMetadata = {
          fullName: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`,
          role: formData?.accountType,
          postcode: formData?.postcode?.trim(),
          city: formData?.city?.trim(),
          emailUpdates: formData?.emailUpdates,
          firstName: formData?.firstName?.trim(),
          lastName: formData?.lastName?.trim()
        };

        const { data, error } = await signUp(
          formData?.email,
          formData?.password,
          registrationMetadata
        );

        if (error) {
          setErrors({ submit: error });
        } else if (data?.user) {
          onSuccess();
          onClose();
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setErrors({ submit: 'Authentication failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setErrors({});
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      postcode: '',
      city: '',
      accountType: 'client',
      agreeToTerms: false,
      agreeToPrivacy: false,
      emailUpdates: false
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-popover rounded-lg shadow-organic-lg">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground">
                {mode === 'login' ? 'Welcome Back' : 'Join Holistic Directories'}
              </h2>
              <p className="text-sm text-muted-foreground font-body mt-1">
                {mode === 'login' ?'Sign in to save businesses and book sessions' :'Create your account to get started'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={formData?.firstName}
                    onChange={handleInputChange}
                    placeholder="First name"
                    error={errors?.firstName}
                    required
                    disabled={isLoading}
                  />
                  <Input
                    label="Last Name"
                    type="text"
                    name="lastName"
                    value={formData?.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                    error={errors?.lastName}
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Account Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Account Type
                  </label>
                  <select
                    name="accountType"
                    value={formData?.accountType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={isLoading}
                  >
                    <option value="client">Client - Looking for holistic services</option>
                    <option value="practitioner">Practitioner - Offering holistic services</option>
                  </select>
                </div>
              </>
            )}

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData?.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                error={errors?.password}
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-gentle"
                disabled={isLoading}
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
              </button>
            </div>

            {mode === 'register' && (
              <>
                <div className="relative">
                  <Input
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData?.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    error={errors?.confirmPassword}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-gentle"
                    disabled={isLoading}
                  >
                    <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
                  </button>
                </div>

                {/* Location */}
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Postcode"
                    type="text"
                    name="postcode"
                    value={formData?.postcode}
                    onChange={handleInputChange}
                    placeholder="SW1A 1AA"
                    error={errors?.postcode}
                    required
                    disabled={isLoading}
                  />
                  <Input
                    label="City (Optional)"
                    type="text"
                    name="city"
                    value={formData?.city}
                    onChange={handleInputChange}
                    placeholder="London"
                    error={errors?.city}
                    disabled={isLoading}
                  />
                </div>

                {/* Terms and Privacy */}
                <div className="space-y-3">
                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData?.agreeToTerms}
                      onChange={handleInputChange}
                      className="mt-1"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-foreground">
                      I agree to the Terms and Conditions
                    </span>
                  </label>
                  {errors?.agreeToTerms && (
                    <p className="text-sm text-destructive">{errors?.agreeToTerms}</p>
                  )}

                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      name="agreeToPrivacy"
                      checked={formData?.agreeToPrivacy}
                      onChange={handleInputChange}
                      className="mt-1"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-foreground">
                      I agree to the Privacy Policy
                    </span>
                  </label>
                  {errors?.agreeToPrivacy && (
                    <p className="text-sm text-destructive">{errors?.agreeToPrivacy}</p>
                  )}

                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      name="emailUpdates"
                      checked={formData?.emailUpdates}
                      onChange={handleInputChange}
                      className="mt-1"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-foreground">
                      I'd like to receive email updates
                    </span>
                  </label>
                </div>
              </>
            )}

            {errors?.submit && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-body">{errors?.submit}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              className="mt-6"
            >
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* Switch Mode */}
          <div className="mt-6 text-center">
            <button
              onClick={switchMode}
              className="text-sm text-primary hover:text-primary/80 font-body transition-gentle"
            >
              {mode === 'login' ? "Don't have an account? Sign up" :"Already have an account? Sign in"
              }
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuthModal;
